import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { format } from "date-fns";

const STORAGE_KEY = "bos_admin_key_v1";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatSGD = (cents) => {
  if (cents == null) return "—";
  return `SGD ${(cents / 100).toFixed(2)}`;
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return format(new Date(iso), "d MMM, HH:mm");
  } catch (_) {
    return iso;
  }
};

const Stat = ({ label, value, hint, testId }) => (
  <div
    data-testid={testId}
    className="border border-bos-ice bg-bos-white rounded-sm px-5 py-5"
  >
    <div className="text-[11px] tracking-[0.16em] uppercase text-bos-mid">{label}</div>
    <div className="mt-2 text-[28px] font-medium text-bos-navy leading-none tracking-tight">
      {value}
    </div>
    {hint && <div className="mt-2 text-[12px] text-bos-charcoal/60">{hint}</div>}
  </div>
);

const SectionTable = ({ title, rows, columns, testId }) => (
  <section data-testid={testId} className="mt-12">
    <h2 className="bos-eyebrow mb-3">{title}</h2>
    {rows.length === 0 ? (
      <p className="text-[13px] text-bos-charcoal/60 border-l border-bos-ice pl-4">
        No rows in this window.
      </p>
    ) : (
      <div className="overflow-x-auto border border-bos-ice rounded-sm">
        <table className="w-full text-[13px]">
          <thead className="bg-bos-ice/50">
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className="px-4 py-3 text-left font-medium text-bos-navy text-[11px] tracking-[0.12em] uppercase"
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-bos-ice/60">
                {columns.map((c) => (
                  <td key={c.key} className="px-4 py-3 text-bos-charcoal/85 align-top">
                    {c.render ? c.render(r) : r[c.key] ?? "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </section>
);

const Login = ({ onSubmit, error }) => {
  const [val, setVal] = useState("");
  return (
    <div className="min-h-[60vh] flex items-center justify-center px-6">
      <form
        data-testid="admin-login-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (val.trim()) onSubmit(val.trim());
        }}
        className="max-w-md w-full border border-bos-ice rounded-sm p-8 bg-bos-white"
      >
        <p className="bos-eyebrow">Admin · BuildOnce Studio</p>
        <h1 className="mt-4 text-[28px] font-medium text-bos-navy leading-tight tracking-tight">
          Enter your admin key.
        </h1>
        <p className="mt-3 text-[14px] text-bos-charcoal/75 leading-[1.7]">
          Read-only operator dashboard. The key lives in your production
          environment variables.
        </p>
        <input
          type="password"
          autoFocus
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder="Admin key"
          data-testid="admin-login-input"
          className="mt-6 w-full border border-bos-ice bg-bos-white text-bos-charcoal text-[14px] px-4 py-3 rounded-sm focus:outline-none focus:border-bos-mid transition-colors"
        />
        {error && (
          <p className="mt-3 text-[13px] text-red-600" data-testid="admin-login-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          data-testid="admin-login-submit"
          className="mt-5 w-full bg-bos-navy text-bos-white text-[14px] font-medium py-3 rounded-sm hover:bg-bos-primary transition-colors"
        >
          Sign in
        </button>
      </form>
    </div>
  );
};

const noop = () => {};
const safeWrite = (k, v) => { try { localStorage.setItem(k, v); } catch (_e) { noop(); } };
const safeRemove = (k) => { try { localStorage.removeItem(k); } catch (_e) { noop(); } };
const safeRead = (k) => { try { return localStorage.getItem(k) || ""; } catch (_e) { return ""; } };

export default function Admin() {
  const [adminKey, setAdminKey] = useState(() => safeRead(STORAGE_KEY));
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(7);

  useEffect(() => {
    document.title = "Admin · BuildOnce Studio";
  }, []);

  const load = useCallback(
    async (key, windowDays) => {
      setLoading(true);
      setError("");
      try {
        const resp = await axios.get(`${API}/admin/stats?days=${windowDays}`, {
          headers: { Authorization: `Bearer ${key}` },
        });
        setStats(resp.data);
        safeWrite(STORAGE_KEY, key);
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401) {
          setError("That key did not work. Try again.");
          setAdminKey("");
          safeRemove(STORAGE_KEY);
        } else if (status === 503) {
          setError("Admin dashboard is not configured on the server.");
        } else {
          setError("Could not load stats. Try again in a moment.");
        }
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (adminKey) load(adminKey, days);
  }, [adminKey, days, load]);

  const onLogin = (key) => {
    setAdminKey(key);
  };
  const onSignOut = () => {
    setAdminKey("");
    setStats(null);
    safeRemove(STORAGE_KEY);
  };

  if (!adminKey) return <Login onSubmit={onLogin} error={error} />;

  const t = stats?.totals || {};
  const c = stats?.conversion || {};
  const r = stats?.recent || {};

  const fmtPct = (v) => (v == null ? "—" : `${v}%`);

  return (
    <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-16" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
        <div>
          <p className="bos-eyebrow">Admin</p>
          <h1 className="mt-3 text-3xl sm:text-4xl font-medium text-bos-navy tracking-tight leading-tight">
            Last <span className="text-bos-primary">{days}</span> days at a glance.
          </h1>
        </div>
        <div className="flex items-center gap-3 text-[13px]">
          <label className="text-bos-charcoal/70">Window</label>
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            data-testid="admin-window-select"
            className="border border-bos-ice bg-bos-white px-3 py-2 rounded-sm focus:outline-none focus:border-bos-mid"
          >
            <option value={1}>1 day</option>
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
          </select>
          <button
            onClick={() => load(adminKey, days)}
            data-testid="admin-refresh-btn"
            className="border border-bos-ice px-3 py-2 rounded-sm hover:border-bos-mid transition-colors"
          >
            Refresh
          </button>
          <button
            onClick={onSignOut}
            data-testid="admin-signout-btn"
            className="text-bos-primary bos-link-underline"
          >
            Sign out
          </button>
        </div>
      </div>

      {loading && !stats && (
        <p data-testid="admin-loading" className="text-bos-charcoal/60">Loading…</p>
      )}
      {error && stats == null && (
        <p data-testid="admin-error" className="text-red-600 text-[14px]">{error}</p>
      )}

      {stats && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat testId="admin-stat-visits" label="Kit page visits" value={t.kit_page_visits ?? 0} hint={`${t.stripe_click_throughs ?? 0} click-throughs to Stripe`} />
            <Stat testId="admin-stat-purchases" label="Kit purchases" value={t.kit_purchases ?? 0} hint={formatSGD(t.kit_revenue_cents) + " revenue"} />
            <Stat testId="admin-stat-diagnostics" label="Diagnostics" value={t.diagnostic_submissions ?? 0} hint={`${t.diagnostic_personal_note_requested ?? 0} requested a personal note`} />
            <Stat testId="admin-stat-ai-tokens" label="AI tokens" value={(t.ai_tokens_input ?? 0) + (t.ai_tokens_output ?? 0)} hint={`in: ${t.ai_tokens_input ?? 0} · out: ${t.ai_tokens_output ?? 0}`} />
          </div>

          {/* Conversion funnel row */}
          <section
            data-testid="admin-conversion-funnel"
            className="mt-8 bg-bos-ice/40 border border-bos-light/40 rounded-sm px-6 py-6"
          >
            <p className="bos-eyebrow mb-4">Funnel · last {days} days</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-y-5 sm:gap-x-8 items-end">
              <div data-testid="admin-funnel-visit-to-click">
                <div className="text-[11px] tracking-[0.12em] uppercase text-bos-mid">Visit → Stripe click</div>
                <div className="mt-2 text-[24px] font-medium text-bos-navy leading-none">
                  {fmtPct(c.visit_to_click_pct)}
                </div>
                <div className="mt-2 text-[12px] text-bos-charcoal/65">
                  {(t.stripe_click_throughs ?? 0)} of {(t.kit_page_visits ?? 0)}
                </div>
              </div>
              <div data-testid="admin-funnel-click-to-purchase">
                <div className="text-[11px] tracking-[0.12em] uppercase text-bos-mid">Stripe click → Purchase</div>
                <div className="mt-2 text-[24px] font-medium text-bos-navy leading-none">
                  {fmtPct(c.click_to_purchase_pct)}
                </div>
                <div className="mt-2 text-[12px] text-bos-charcoal/65">
                  {(t.kit_purchases ?? 0)} of {(t.stripe_click_throughs ?? 0)}
                </div>
              </div>
              <div data-testid="admin-funnel-visit-to-purchase">
                <div className="text-[11px] tracking-[0.12em] uppercase text-bos-mid">Visit → Purchase (end-to-end)</div>
                <div className="mt-2 text-[24px] font-medium text-bos-primary leading-none">
                  {fmtPct(c.visit_to_purchase_pct)}
                </div>
                <div className="mt-2 text-[12px] text-bos-charcoal/65">
                  {(t.kit_purchases ?? 0)} of {(t.kit_page_visits ?? 0)}
                </div>
              </div>
            </div>
            <p className="mt-5 text-[11px] text-bos-charcoal/45 tracking-[0.03em]">
              Percentages are shown only when the prior step has data in this window.
              Dashes mean we do not have enough volume yet.
            </p>
          </section>

          <SectionTable
            testId="admin-recent-purchases"
            title="Recent kit purchases"
            rows={r.kit_tokens || []}
            columns={[
              { key: "created_at", label: "When", render: (row) => formatDate(row.created_at) },
              { key: "email", label: "Email" },
              { key: "amount_paid_cents", label: "Amount", render: (row) => formatSGD(row.amount_paid_cents) },
              { key: "stripe_session_id", label: "Stripe session", render: (row) => (row.stripe_session_id || "—").slice(0, 22) + (row.stripe_session_id?.length > 22 ? "…" : "") },
              { key: "last_accessed_at", label: "Last accessed", render: (row) => formatDate(row.last_accessed_at) },
            ]}
          />

          <SectionTable
            testId="admin-recent-diagnostics"
            title="Recent diagnostics"
            rows={r.diagnostics || []}
            columns={[
              { key: "created_at", label: "When", render: (row) => formatDate(row.created_at) },
              { key: "name", label: "Name" },
              { key: "email", label: "Email" },
              { key: "business", label: "Business" },
              { key: "reflection_source", label: "Source" },
              { key: "personal_note_requested", label: "Personal note?", render: (row) => row.personal_note_requested ? <span className="text-bos-primary font-medium">★ requested</span> : <span className="text-bos-charcoal/40">—</span> },
            ]}
          />

          <SectionTable
            testId="admin-recent-contact"
            title="Recent contact"
            rows={r.contact || []}
            columns={[
              { key: "created_at", label: "When", render: (row) => formatDate(row.created_at) },
              { key: "name", label: "Name" },
              { key: "email", label: "Email" },
              { key: "subject", label: "Subject" },
            ]}
          />

          <SectionTable
            testId="admin-recent-bookings"
            title="Recent bookings"
            rows={r.bookings || []}
            columns={[
              { key: "created_at", label: "When", render: (row) => formatDate(row.created_at) },
              { key: "name", label: "Name" },
              { key: "email", label: "Email" },
              { key: "topic", label: "Topic" },
              { key: "date", label: "Date" },
            ]}
          />

          {stats.window && (
            <p className="mt-12 text-[11px] text-bos-charcoal/45 tracking-[0.04em]">
              Window since {formatDate(stats.window.since)} · {stats.window.days}-day rolling
            </p>
          )}
        </>
      )}
    </div>
  );
}
